import { Point } from 'geojson';

import { SnLonLat, SnLonLatBounds } from '@lib/geo/helpers';

import { lonLatToArcGIS, lonLatBoundsToArcGIS, geojsonToArcGIS } from '../helpers';
import { QueryGeometry } from './query-geometry';
import { IQueryOptions } from './query-options';

export class Query {
  private readonly _options: IQueryOptions;

  constructor(options?: IQueryOptions) {
    this._options = {
      where: '1=1',
      outFields: '*',
      returnGeometry: true,
      ...options,
    };
  }

  public fetch(fetch: any): this {
    this._options.fetch = fetch;
    return this;
  }

  public params(params: any): this {
    this._options.params = params;
    return this;
  }

  public headers(headers: any): this {
    this._options.headers = headers;
    return this;
  }

  public within(geometry: QueryGeometry): this {
    this._setGeometry(geometry);
    this._options.spatialRel = 'esriSpatialRelWithin';

    return this;
  }

  public intersects(geometry: QueryGeometry): this {
    this._setGeometry(geometry);
    this._options.spatialRel = 'esriSpatialRelIntersects';

    return this;
  }

  public contains(geometry: QueryGeometry): this {
    this._setGeometry(geometry);
    this._options.spatialRel = 'esriSpatialRelContains';

    return this;
  }

  public crosses(geometry: QueryGeometry): this {
    this._setGeometry(geometry);
    this._options.spatialRel = 'esriSpatialRelCrosses';

    return this;
  }

  public touches(geometry: SnLonLat | SnLonLatBounds): this {
    this._setGeometry(geometry);
    this._options.spatialRel = 'esriSpatialRelTouches';

    return this;
  }

  public overlaps(geometry: QueryGeometry): this {
    this._setGeometry(geometry);
    this._options.spatialRel = 'esriSpatialRelOverlaps';

    return this;
  }

  public where(where: string): this {
    this._options.where = where;

    return this;
  }

  public offset(offset: number): this {
    this._options.resultOffset = offset;

    return this;
  }

  public limit(limit: number): this {
    this._options.resultRecordCount = limit;

    return this;
  }

  public precision(precision: number): this {
    this._options.geometryPrecision = precision;

    return this;
  }

  public featureIds(ids: number[]): this {
    this._options.objectIds = ids;

    return this;
  }

  public fields(fields: '*' | string[]): this {
    this._options.outFields = fields;

    return this;
  }

  public nearby(geometry: SnLonLat | Point, radius: number): this {
    this.intersects(geometry);

    this._options.units = 'esriSRUnit_Meter';
    this._options.distance = radius;

    return this;
  }

  public between(start: Date, end: Date): this {
    this._options.time = [start.valueOf(), end.valueOf()];

    return this;
  }

  public orderBy(fieldName: string, order: 'ASC' | 'DESC' = 'ASC'): this {
    const orderBy = [fieldName, order].join(' ');

    this._options.orderByFields = this._options.orderByFields
      ? this._options.orderByFields.concat(',', orderBy)
      : orderBy;

    return this;
  }

  public distinct(): this {
    this._options.returnGeometry = false;
    this._options.returnDistinctValues = true;

    return this;
  }

  public toJSON(): IQueryOptions {
    return this._options;
  }

  private _setGeometry(geometry: QueryGeometry): void {
    let arcGISGeometry;

    if (geometry instanceof SnLonLat) {
      arcGISGeometry = lonLatToArcGIS(geometry);
    } else if (geometry instanceof SnLonLatBounds) {
      arcGISGeometry = lonLatBoundsToArcGIS(geometry);
    } else {
      arcGISGeometry = geojsonToArcGIS(geometry);
    }

    this._options.inSR = { wkid: 4326 };
    this._options.geometry = arcGISGeometry.geometry;
    this._options.geometryType = arcGISGeometry.geometryType;
  }
}
